import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import {
  Modal, Form, Input, Select, Button, Row, Col,
  Tag, Typography, Tabs, Space, notification, Divider,
  Tooltip, Popconfirm,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, InfoCircleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  addConcept, updateConcept, getVariables, getPlanVariables,
} from '../../../api/payments';
import { BOOKING_FIELDS, BOOKING_VARIABLES } from '../../FinanceEngine/constants/booking.constants';
import { OPTIONS } from '../../FinanceEngine/constants/common.constants';
import { EConceptType } from '../../FinanceEngine/enums/types.enum';

const { Text, Paragraph } = Typography;
const { TabPane } = Tabs;

interface ConceptEditorModalProps {
  visible: boolean;
  concept: any;
  planID: string;
  conceptType: EConceptType;
  onClose: (updatedConcept?: any) => void;
}

interface VariableItem {
  _id: string;
  name: string;
  keyName?: string;
  value: any;
  planID?: string;
  origin?: string;
  type?: string;
}

const FREQUENCY_OPTIONS = [
  { label: 'Unique (per booking)', value: 'UNIQUE' },
];

const formatOperationPreview = (
  operation: string,
  planVariables: VariableItem[],
  globalVariables: VariableItem[],
): string => {
  if (!operation) return '';
  let formatted = operation;

  const variableExps = operation.match(/\{\{variable\.(\w+)\}\}/g);
  if (variableExps) {
    variableExps.forEach((exp) => {
      const id = exp.replace('{{variable.', '').replace('}}', '');
      const found = [...planVariables, ...globalVariables].find((v) => v._id === id);
      if (found) {
        const color = found.planID ? '#C9D6E8' : '#87d068';
        formatted = formatted.replace(
          exp,
          `<span style="color: ${color}; font-weight: 600; background: rgba(135,208,104,0.1); padding: 1px 6px; border-radius: 3px;">${found.name}</span>`,
        );
      } else {
        formatted = formatted.replace(
          exp,
          `<span style="color: #ff4d4f; font-weight: 600; text-decoration: line-through;">${exp}</span>`,
        );
      }
    });
  }

  const bookingExps = operation.match(/\{\{booking\.([^}]+)\}\}/g);
  if (bookingExps) {
    bookingExps.forEach((exp) => {
      const fieldId = exp.replace('{{booking.', '').replace('}}', '');
      const found = BOOKING_VARIABLES.find((v) => v._id === fieldId);
      const label = found ? found.name : fieldId;
      formatted = formatted.replace(
        exp,
        `<span style="color: #2db7f5; font-weight: 600; background: rgba(45,183,245,0.1); padding: 1px 6px; border-radius: 3px;">${label}</span>`,
      );
    });
  }

  return formatted;
};

const ConceptEditorModal: React.FC<ConceptEditorModalProps> = ({
  visible, concept, planID, conceptType, onClose,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [planVariables, setPlanVariables] = useState<VariableItem[]>([]);
  const [globalVariables, setGlobalVariables] = useState<VariableItem[]>([]);
  const [operation, setOperation] = useState('');
  const operationRef = useRef<HTMLTextAreaElement>(null);

  const isEditing = !!concept?._id;

  const fetchVariables = useCallback(async () => {
    try {
      const planVarsRes = await getPlanVariables(planID);
      setPlanVariables(planVarsRes.data?.data || planVarsRes.data || []);
    } catch {
      notification.error({ message: 'Error cargando variables del plan' });
    }

    try {
      const globalVarsRes = await getVariables({ origin: conceptType, limit: 200, page: 1 });
      const allVars: VariableItem[] = globalVarsRes.data?.data?.data || globalVarsRes.data?.data || [];
      setGlobalVariables(allVars.filter((v) => !v.planID));
    } catch {
      // endpoint puede fallar — no bloquear el resto del modal
    }
  }, [planID, conceptType]);

  useEffect(() => {
    if (visible && planID) {
      fetchVariables();
    }
  }, [visible, planID, fetchVariables]);

  useEffect(() => {
    if (!concept) return;
    form.setFieldsValue({
      name: concept.name || '',
      frequency: concept.frequency || 'UNIQUE',
      conditions: concept.conditions || [],
    });
    setOperation(concept.operation || '');
  }, [concept, form]);

  const insertVariable = (type: string, variable: any) => {
    const template = `{{${type}.${variable._id}}}`;
    const textarea = operationRef.current;

    if (textarea) {
      const start = textarea.selectionStart || 0;
      const end = textarea.selectionEnd || 0;
      const currentOp = operation || '';
      const newOp = `${currentOp.substring(0, start)}${template}${currentOp.substring(end)}`;
      setOperation(newOp);
      setTimeout(() => {
        const newPos = start + template.length;
        textarea.focus();
        textarea.setSelectionRange(newPos, newPos);
      }, 0);
    } else {
      setOperation(`${operation || ''} ${template}`);
    }
  };

  const handleSave = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }

    setLoading(true);
    const values = form.getFieldsValue();
    const conditions = (values.conditions || []).filter(
      (c: any) => c?.field && c?.operator,
    );

    const payload = {
      ...values,
      conditions,
      operation,
      planID,
      type: conceptType,
    };

    try {
      if (isEditing) {
        const { data } = await updateConcept(concept._id, payload);
        notification.success({ message: 'Concepto actualizado' });
        onClose(data.data);
      } else {
        const { data } = await addConcept(payload);
        notification.success({ message: 'Concepto creado' });
        onClose(data.data);
      }
    } catch {
      notification.error({ message: 'Error guardando concepto' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setOperation('');
    onClose();
  };

  const formattedPreview = formatOperationPreview(operation, planVariables, globalVariables);

  const renderConditionsSection = () => (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Text strong style={{ fontSize: 14 }}>Condiciones</Text>
        <Tooltip title="Todas las condiciones deben cumplirse (AND) para que el concepto se aplique al booking">
          <InfoCircleOutlined style={{ marginLeft: 8, color: '#8c8c8c' }} />
        </Tooltip>
      </div>
      <Form.List name="conditions" initialValue={[]}>
        {(fields, { add, remove }) => (
          <>
            {fields.map((field, idx) => (
              <div
                key={field.key}
                style={{
                  background: '#1f1f1f',
                  border: '1px solid #303030',
                  borderRadius: 8,
                  padding: '12px 16px',
                  marginBottom: 8,
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
                >
                  <Tag color="blue" style={{ fontSize: 11 }}>
                    {`CONDICIÓN ${idx + 1}`}
                  </Tag>
                  <Popconfirm
                    title="¿Eliminar esta condición?"
                    onConfirm={() => remove(field.name)}
                    okText="Sí"
                    cancelText="No"
                  >
                    <Button
                      size="small"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                    />
                  </Popconfirm>
                </div>
                <Row gutter={12}>
                  <Col span={8}>
                    <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                      CAMPO DEL BOOKING
                    </Text>
                    <Form.Item
                      {...field}
                      name={[field.name, 'field']}
                      rules={[{ required: true, message: 'Requerido' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        showSearch
                        optionFilterProp="children"
                        options={BOOKING_FIELDS}
                        placeholder="Seleccionar campo"
                        size="small"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                      OPERADOR
                    </Text>
                    <Form.Item
                      {...field}
                      name={[field.name, 'operator']}
                      rules={[{ required: true, message: 'Requerido' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        options={OPTIONS}
                        placeholder="Seleccionar"
                        size="small"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                      VALOR A COMPARAR
                    </Text>
                    <Form.Item
                      {...field}
                      name={[field.name, 'value']}
                      rules={[{ required: true, message: 'Requerido' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input placeholder="ej: SMALL_FLEET, CASH, EC" size="small" />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            ))}
            <Button
              type="dashed"
              onClick={() => add()}
              block
              icon={<PlusOutlined />}
              style={{ borderColor: '#434343' }}
            >
              Agregar Condición
            </Button>
            {fields.length === 0 && (
              <Text
                type="secondary"
                style={{
                  fontSize: 12, display: 'block', marginTop: 8, textAlign: 'center',
                }}
              >
                Sin condiciones = se aplica a todos los bookings del plan
              </Text>
            )}
          </>
        )}
      </Form.List>
    </div>
  );

  const renderVariableTags = () => (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Text strong style={{ fontSize: 14 }}>Variables Disponibles</Text>
        <Tooltip title="Click en una variable para insertarla en la fórmula. Se insertará en la posición del cursor.">
          <InfoCircleOutlined style={{ marginLeft: 8, color: '#8c8c8c' }} />
        </Tooltip>
      </div>
      <Tabs defaultActiveKey="booking" size="small">
        <TabPane
          tab={(
            <span>
              <span style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#2db7f5',
                marginRight: 6,
              }}
              />
              Booking
            </span>
          )}
          key="booking"
        >
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 120, overflow: 'auto',
          }}
          >
            {BOOKING_VARIABLES.map((v) => (
              <Tag
                key={v._id}
                color="#2db7f5"
                onClick={() => insertVariable('booking', v)}
                style={{ cursor: 'pointer', margin: 0 }}
              >
                <Text style={{ color: '#1A1F36', fontSize: 12 }} strong>
                  {v.name}
                </Text>
              </Tag>
            ))}
          </div>
        </TabPane>
        <TabPane
          tab={(
            <span>
              <span style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#C9D6E8',
                marginRight: 6,
              }}
              />
              {`Plan (${planVariables.length})`}
            </span>
          )}
          key="plan"
        >
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 120, overflow: 'auto',
          }}
          >
            {planVariables.length > 0 ? planVariables.map((v) => (
              <Tag
                key={v._id}
                color="#C9D6E8"
                onClick={() => insertVariable('variable', v)}
                style={{ cursor: 'pointer', margin: 0 }}
              >
                <Text style={{ color: '#1A1F36', fontSize: 12 }} strong>
                  {v.name}
                </Text>
              </Tag>
            )) : (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Este plan no tiene variables propias
              </Text>
            )}
          </div>
        </TabPane>
        <TabPane
          tab={(
            <span>
              <span style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#87d068',
                marginRight: 6,
              }}
              />
              {`Globales (${globalVariables.length})`}
            </span>
          )}
          key="global"
        >
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 120, overflow: 'auto',
          }}
          >
            {globalVariables.length > 0 ? globalVariables.map((v) => (
              <Tag
                key={v._id}
                color="#87d068"
                onClick={() => insertVariable('variable', v)}
                style={{ cursor: 'pointer', margin: 0 }}
              >
                <Text style={{ color: '#1A1F36', fontSize: 12 }} strong>
                  {v.name}
                </Text>
              </Tag>
            )) : (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {'No hay variables globales de tipo '}
                {conceptType}
              </Text>
            )}
          </div>
        </TabPane>
      </Tabs>
    </div>
  );

  const renderFormulaEditor = () => (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Text strong style={{ fontSize: 14 }}>Fórmula (Operation)</Text>
        <Tooltip title="Expresión JavaScript que se evalúa con los valores del booking y variables. Usa +, -, *, / y paréntesis.">
          <InfoCircleOutlined style={{ marginLeft: 8, color: '#8c8c8c' }} />
        </Tooltip>
      </div>
      <Input.TextArea
        ref={operationRef as any}
        value={operation}
        onChange={(e) => setOperation(e.target.value)}
        placeholder="ej: {{booking.orderAmount}} * {{variable.abc123}} / 100"
        autoSize={{ minRows: 3, maxRows: 6 }}
        style={{
          fontFamily: "'Courier New', monospace",
          fontSize: 13,
          background: '#1f1f1f',
          borderColor: '#434343',
        }}
      />

      {operation && (
        <div style={{
          marginTop: 12,
          padding: 12,
          background: '#1f1f1f',
          border: '1px solid #303030',
          borderRadius: 8,
        }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
          }}
          >
            <ThunderboltOutlined style={{ color: '#FAAD14' }} />
            <Text type="secondary" style={{ fontSize: 11 }}>PREVIEW DE LA FÓRMULA</Text>
          </div>
          <div
            style={{
              fontFamily: "'Courier New', monospace",
              fontSize: 13,
              lineHeight: '22px',
              wordBreak: 'break-all',
            }}
            dangerouslySetInnerHTML={{ __html: formattedPreview }}
          />
        </div>
      )}

    </div>
  );

  return (
    <Modal
      title={isEditing ? `Editar: ${concept?.name}` : 'Crear Concepto'}
      open={visible}
      width={900}
      onCancel={handleCancel}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={handleCancel}>Cancelar</Button>,
        <Button key="save" type="primary" loading={loading} onClick={handleSave}>
          {isEditing ? 'Actualizar' : 'Crear'}
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ frequency: 'UNIQUE', conditions: [] }}
      >
        <Row gutter={16}>
          <Col span={16}>
            <Form.Item
              label="Nombre del concepto"
              name="name"
              rules={[{ required: true, message: 'El nombre es requerido' }]}
            >
              <Input placeholder="ej: Tarifa de envío, Comisión por servicio" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Frecuencia" name="frequency">
              <Select options={FREQUENCY_OPTIONS} />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: '8px 0 16px' }} />

        {renderConditionsSection()}
      </Form>

      <Divider style={{ margin: '16px 0' }} />

      {renderVariableTags()}

      <Divider style={{ margin: '16px 0' }} />

      {renderFormulaEditor()}
    </Modal>
  );
};

export { ConceptEditorModal };
